/**
 * Incremental parser for OpenAI-style `text/event-stream` bodies.
 *
 * Split out from the HTTP client so it can be tested against hand-written
 * chunk boundaries — the interesting bugs here all live in the case where a
 * JSON payload is bisected across two network reads.
 */

const DONE_SENTINEL = "[DONE]";

export interface SseEvent {
  readonly data: string;
}

/**
 * Feed raw text chunks; get back complete `data:` payloads.
 *
 * Holds a buffer across calls because a chunk boundary lands wherever TCP
 * decides, not on an event boundary.
 */
export class SseDecoder {
  #buffer = "";

  /** Returns the payloads completed by this chunk, in order. */
  push(chunk: string): readonly string[] {
    this.#buffer += chunk;
    const payloads: string[] = [];

    // Events are separated by a blank line; tolerate CRLF from proxies.
    let separator = this.#nextSeparator();
    while (separator !== null) {
      const raw = this.#buffer.slice(0, separator.index);
      this.#buffer = this.#buffer.slice(separator.index + separator.length);

      const payload = extractData(raw);
      if (payload !== null) payloads.push(payload);

      separator = this.#nextSeparator();
    }

    return payloads;
  }

  /** Anything left after the response body ends (servers often omit the last blank line). */
  flush(): readonly string[] {
    const remainder = this.#buffer;
    this.#buffer = "";
    const payload = extractData(remainder);
    return payload === null ? [] : [payload];
  }

  #nextSeparator(): { index: number; length: number } | null {
    const lf = this.#buffer.indexOf("\n\n");
    const crlf = this.#buffer.indexOf("\r\n\r\n");

    if (crlf !== -1 && (lf === -1 || crlf < lf)) {
      return { index: crlf, length: 4 };
    }
    if (lf !== -1) return { index: lf, length: 2 };
    return null;
  }
}

/**
 * Pull the `data:` field out of one event block, joining multi-line data per
 * the SSE spec. Comment lines (`:` keep-alives) and other fields are dropped.
 */
function extractData(block: string): string | null {
  const lines = block.split(/\r?\n/);
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith(":")) continue;
    if (!line.startsWith("data:")) continue;
    // A single optional space after the colon is part of the framing.
    const value = line.slice(5);
    dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
  }

  if (dataLines.length === 0) return null;
  const joined = dataLines.join("\n");
  return joined.trim().length === 0 ? null : joined;
}

export function isDone(payload: string): boolean {
  return payload.trim() === DONE_SENTINEL;
}

/**
 * Turn a byte stream into decoded `data:` payloads, stopping at `[DONE]`.
 * Malformed payloads are skipped rather than thrown — one bad keep-alive frame
 * should not abort a working answer.
 */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<string, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const sse = new SseDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      for (const payload of sse.push(decoder.decode(value, { stream: true }))) {
        if (isDone(payload)) return;
        yield payload;
      }
    }

    for (const payload of sse.flush()) {
      if (isDone(payload)) return;
      yield payload;
    }
  } finally {
    reader.releaseLock();
  }
}
